/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import hasHasInstanceSymbolSupport from '@stdlib/assert-has-has-instance-symbol-support'; // eslint-disable-line id-length
import HasInstanceSymbol from '@stdlib/symbol-has-instance';
import format from '@stdlib/error-tools-fmtprodmsg';


// VARIABLES //

var hasSupport = hasHasInstanceSymbolSupport();


// MAIN //

/**
* Tests whether a value has in its prototype chain a specified constructor as a prototype property.
*
* @param {*} value - value to test
* @param {Function} constructor - constructor to test against
* @throws {TypeError} constructor must be callable
* @returns {boolean} boolean indicating whether a value is an instance of a provided constructor
*
* @example
* var bool = instanceOf( [], Array );
* // returns true
*
* @example
* var bool = instanceOf( {}, Object ); // exception
* // returns true
*
* @example
* var bool = instanceOf( 'beep', String );
* // returns false
*
* @example
* var bool = instanceOf( null, Object );
* // returns false
*
* @example
* var bool = instanceOf( 5, Object );
* // returns false
*/
function instanceOf( value, constructor ) {
	// TODO: replace with `isCallable` check
	if (
		typeof constructor !== 'function' &&
		!(
			hasSupport &&
			typeof constructor === 'object' &&
			typeof constructor[ HasInstanceSymbol ] === 'function'
		)
	) {
		throw new TypeError( format( '03v3E', constructor ) );
	}
	return ( value instanceof constructor );
}


// EXPORTS //

export default instanceOf;
